'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';

type ToastVariant = 'default' | 'success' | 'error';

export interface MHToastItem {
  id: string;
  title: string;
  description?: string;
  variant?: ToastVariant;
}

interface MHToastContextValue {
  showToast: (toast: Omit<MHToastItem, 'id'>) => void;
}

const MHToastContext = React.createContext<MHToastContextValue | null>(null);

export function useMHToast() {
  const context = React.useContext(MHToastContext);
  if (!context) throw new Error('useMHToast must be used within MHToastProvider');
  return context;
}

export function MHToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<MHToastItem[]>([]);

  const showToast = React.useCallback((toast: Omit<MHToastItem, 'id'>) => {
    const id = crypto.randomUUID();
    setToasts((current) => [...current, { ...toast, id }]);

    window.setTimeout(() => {
      setToasts((current) => current.filter((item) => item.id !== id));
    }, 5000);
  }, []);

  return (
    <MHToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed right-4 top-4 z-[60] flex w-[320px] max-w-[calc(100vw-2rem)] flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cn(
              'rounded-xl border border-border-subtle bg-surface-1 p-4 shadow-sm',
              toast.variant === 'success' && 'text-text-1',
              toast.variant === 'error' && 'text-text-1'
            )}
          >
            <p className="text-[14px] font-semibold text-text-1">{toast.title}</p>
            {toast.description ? (
              <p className="mt-1 text-[13px] font-medium text-text-3">{toast.description}</p>
            ) : null}
          </div>
        ))}
      </div>
    </MHToastContext.Provider>
  );
}
